import { Link } from 'react-router'
import { AssociationCard } from '@/components/patterns/AssociationCard'
import { Button } from '@/components/ui/button'
import { openSignedDocument } from '@/features/preinvest/documents/openSignedDocument'
import { toast } from '@/lib/toast'
import { getScanPhotoUrl } from '../api'
import { amsterdamDay, shortDay } from '../record/format'
import { ListsCard } from '../record/ListsCard'
import { startupRoleLabel } from '../record/labels'
import type { CrmContactCardScan, CrmContactDetail, CrmStartupLinkDetail } from '../types'
import { OrganisationsCard } from './OrganisationsCard'

const linkClass = 'font-medium underline-offset-2 hover:underline'
// The names under "Heeft geïntroduceerd"; the count above them is the total.
const MAX_INTRODUCED = 5

/**
 * The right column of the contact page (design §4.5): Organisaties (editable), Lijsten
 * (editable: add to and take off static lists), Op het platform (only with a startup link), Introducties (read-only; edit
 * them in the section Relatie) and Visitekaartjes (only with scans).
 */
export function ContactAssociations({
  detail,
  scans,
  linkRequest,
  onChanged,
}: {
  detail: CrmContactDetail
  scans: CrmContactCardScan[]
  /** A new number asks the Organisaties card to open its link form (Meer ▾). */
  linkRequest: number
  onChanged: () => void
}) {
  return (
    <>
      <OrganisationsCard detail={detail} onChanged={onChanged} linkRequest={linkRequest} />
      <ListsCard lists={detail.lists} editing={{ personId: detail.person_id, onChanged }} />
      {detail.startup_links.length > 0 ? <PlatformCard links={detail.startup_links} /> : null}
      <IntroductionsCard detail={detail} />
      {scans.length > 0 ? <CardScansCard scans={scans} /> : null}
    </>
  )
}

/** "Op het platform" (ED-04): the startup dossiers this person belongs to. */
function PlatformCard({ links }: { links: CrmStartupLinkDetail[] }) {
  return (
    <AssociationCard
      title="Op het platform"
      items={links}
      itemKey={(link) => `${link.startup_id}:${link.role}`}
      emptyText="Geen koppelingen."
      renderItem={(link) => (
        <div className="flex flex-col text-sm">
          {link.deal_id ? (
            <Link to={`/preinvest/deals/${link.deal_id}`} className={linkClass}>
              {link.name}
            </Link>
          ) : (
            <span className="font-medium">{link.name}</span>
          )}
          <span className="text-muted-foreground">{startupRoleLabel(link.role)}</span>
        </div>
      )}
    />
  )
}

/** Who introduced this contact and whom this contact introduced; changing it is done in Relatie. */
function IntroductionsCard({ detail }: { detail: CrmContactDetail }) {
  const { introduced_by: by, introduced } = detail
  return (
    <section aria-label="Introducties" className="vonk-card flex flex-col gap-2 p-4">
      <h2 className="text-sm font-semibold">Introducties</h2>
      <p className="text-sm">
        <span className="text-muted-foreground">Geïntroduceerd door: </span>
        {by ? (
          <Link to={`/relaties/contacten/${by.id}`} className={linkClass}>
            {by.full_name}
          </Link>
        ) : (
          '—'
        )}
      </p>
      <p className="text-sm">
        <span className="text-muted-foreground">Heeft geïntroduceerd: </span>
        {introduced.length}
      </p>
      {introduced.length > 0 ? (
        <ul className="flex flex-col gap-1 text-sm">
          {introduced.slice(0, MAX_INTRODUCED).map((person) => (
            <li key={person.person_id}>
              <Link to={`/relaties/contacten/${person.person_id}`} className={linkClass}>
                {person.full_name}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

/**
 * Visitekaartjes (crm-events-kaartscan): the card scans this contact came from, with the
 * event they were scanned at and a link to the stored photo (a signed URL). The event link
 * goes to the event page until E5 puts events in lists.
 */
function CardScansCard({ scans }: { scans: CrmContactCardScan[] }) {
  // `openSignedDocument` opens the tab synchronously (before the await), so iOS Safari's
  // pop-up blocker does not lose the click gesture.
  async function openPhoto(scanId: string) {
    try {
      await openSignedDocument(() => getScanPhotoUrl(scanId).then((response) => response.url))
    } catch {
      toast.error('Kon de foto niet openen.')
    }
  }

  return (
    <AssociationCard
      title="Visitekaartjes"
      items={scans}
      itemKey={(scan) => scan.id}
      emptyText="Geen visitekaartjes."
      renderItem={(scan) => (
        <div className="flex items-center gap-2 text-sm">
          <Link to={`/relaties/events/${scan.event_id}`} className={`min-w-0 flex-1 truncate ${linkClass}`}>
            {scan.event_name}
          </Link>
          <span className="shrink-0 text-muted-foreground">{shortDay(amsterdamDay(scan.created_at))}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => void openPhoto(scan.id)}>
            Foto
          </Button>
        </div>
      )}
    />
  )
}
